<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="internal" @tree-node-click="treeNodeClick"></DepartmentPane>
    </section>
    <section class="dp-right summary-right">
      <div class="summary-head">
        <div class="mode-seg" role="tablist">
          <button
            type="button"
            class="mode-seg-item"
            :class="{ 'is-active': viewMode === 'weekly' }"
            @click="switchMode('weekly')">
            <i class="el-icon-date"></i>
            <span>按周汇总</span>
          </button>
          <button
            type="button"
            class="mode-seg-item"
            :class="{ 'is-active': viewMode === 'daily' }"
            @click="switchMode('daily')">
            <i class="el-icon-s-data"></i>
            <span>按日统计</span>
          </button>
        </div>
        <div class="mode-hint">
          <i class="el-icon-info"></i>
          <span v-if="viewMode === 'weekly'">按自然周（周一至周日）分行统计应打、实打、缺次；参考应打按当前人员与项目配置计算，仅供核算参考。</span>
          <span v-else>查看某一天某个项目的应到名单：谁已打、谁未打、谁免签，适合升国旗等当天核对。</span>
        </div>
      </div>

      <!-- 按周汇总 -->
      <template v-if="viewMode === 'weekly'">
        <div class="toolbar-filter-bar">
          <el-form :inline="true" :model="filters" class="toolbar-filter-form">
            <div class="toolbar-filter-fields">
              <el-form-item label="周范围">
                <el-date-picker
                  ref="weekRangePicker"
                  v-model="filters.dateRange"
                  type="daterange"
                  value-format="yyyy-MM-dd"
                  start-placeholder="开始"
                  end-placeholder="结束"
                  align="left"
                  append-to-body
                  popper-class="htwa-date-popper"
                  class="toolbar-field-w-daterange"
                  @focus="alignDatePanel('weekRangePicker')">
                </el-date-picker>
              </el-form-item>
              <el-form-item label="姓名">
                <el-input
                  v-model="filters.teacherName"
                  clearable
                  placeholder="教职工姓名"
                  class="toolbar-field-w-md"
                  @keyup.enter.native="handleQuery">
                </el-input>
              </el-form-item>
              <el-form-item label="项目">
                <el-select v-model="filters.itemId" placeholder="请选择项目" class="toolbar-field-w-md htwa-item-select" @change="handleQuery">
                  <el-option v-for="item in itemOptions" :key="item.Id" :label="item.ItemName" :value="item.Id"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="补卡">
                <el-select v-model="filters.isMakeup" clearable placeholder="全部" class="toolbar-field-w-sm">
                  <el-option label="仅正常" :value="false"></el-option>
                  <el-option label="仅补卡" :value="true"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">查询</el-button>
              </el-form-item>
            </div>
            <div class="toolbar-filter-actions">
              <el-button size="small" type="warning" icon="el-icon-download" :loading="exporting" @click="handleExport">导出</el-button>
            </div>
          </el-form>
        </div>

        <div class="tbl-scroll-host">
          <el-table key="weekly-table" :data="rows" v-loading="listLoading" stripe height="100%">
            <el-table-column label="周起止" min-width="190" align="center">
              <template slot-scope="scope">
                {{ formatDate(scope.row.WeekStartDate) }} ~ {{ formatDate(scope.row.WeekEndDate) }}
              </template>
            </el-table-column>
            <el-table-column prop="TeacherName" label="教职工" min-width="100" align="center"></el-table-column>
            <el-table-column prop="ClassName" label="班级/类型" min-width="120" align="center"></el-table-column>
            <el-table-column prop="ItemName" label="项目" min-width="120" align="center"></el-table-column>
            <el-table-column prop="RequiredCount" label="参考应打" width="90" align="center"></el-table-column>
            <el-table-column label="总次数" width="80" align="center">
              <template slot-scope="scope">
                <el-link v-if="scope.row.TotalCount > 0" type="primary" :underline="false" @click="openRecordDialog(scope.row, null)">
                  {{ scope.row.TotalCount }}
                </el-link>
                <span v-else>0</span>
              </template>
            </el-table-column>
            <el-table-column label="正常" width="60" align="center">
              <template slot-scope="scope">
                <el-link v-if="scope.row.NormalCount > 0" type="primary" :underline="false" @click="openRecordDialog(scope.row, false)">
                  {{ scope.row.NormalCount }}
                </el-link>
                <span v-else>0</span>
              </template>
            </el-table-column>
            <el-table-column label="补卡" width="60" align="center">
              <template slot-scope="scope">
                <el-link v-if="scope.row.MakeupCount > 0" type="primary" :underline="false" @click="openRecordDialog(scope.row, true)">
                  {{ scope.row.MakeupCount }}
                </el-link>
                <span v-else>0</span>
              </template>
            </el-table-column>
            <el-table-column label="免签" width="60" align="center">
              <template slot-scope="scope">
                <el-tooltip v-if="scope.row.CancelledCount > 0" :content="`本周另有 ${scope.row.CancelledCount} 场停办，已从参考应打中扣除`" placement="top">
                  <span>{{ scope.row.ExemptCount || 0 }}<i class="el-icon-info cancelled-hint"></i></span>
                </el-tooltip>
                <span v-else>{{ scope.row.ExemptCount || 0 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="MissingCount" label="参考缺次" width="90" align="center"></el-table-column>
            <el-table-column label="最后打卡" min-width="160" align="center">
              <template slot-scope="scope">{{ formatDateTime(scope.row.LastPunchTime) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination-container">
          <el-pagination
            layout="total, prev, pager, next"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            style="float: right"
            @current-change="handleCurrentChange">
          </el-pagination>
        </div>
      </template>

      <!-- 按日统计 -->
      <template v-else>
        <div class="toolbar-filter-bar">
          <el-form :inline="true" class="toolbar-filter-form">
            <div class="toolbar-filter-fields">
              <el-form-item label="日期">
                <el-date-picker
                  ref="dailyDatePicker"
                  v-model="daily.date"
                  type="date"
                  value-format="yyyy-MM-dd"
                  :clearable="false"
                  placeholder="选择日期"
                  align="left"
                  append-to-body
                  popper-class="htwa-date-popper"
                  class="toolbar-field-w-date"
                  @focus="alignDatePanel('dailyDatePicker')">
                </el-date-picker>
              </el-form-item>
              <el-form-item label="项目">
                <el-select v-model="daily.itemId" placeholder="请选择项目" class="toolbar-field-w-md htwa-item-select">
                  <el-option v-for="item in itemOptions" :key="item.Id" :label="item.ItemName" :value="item.Id"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="状态">
                <el-select v-model="daily.status" clearable placeholder="全部" class="toolbar-field-w-sm">
                  <el-option v-for="s in dailyStatusOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="姓名">
                <el-input
                  v-model="daily.teacherName"
                  clearable
                  placeholder="教职工姓名"
                  class="toolbar-field-w-md"
                  @keyup.enter.native="handleDailyQuery">
                </el-input>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" size="small" icon="el-icon-search" @click="handleDailyQuery">查询</el-button>
              </el-form-item>
            </div>
            <div class="toolbar-filter-actions">
              <el-button size="small" type="warning" icon="el-icon-download" :loading="dailyExporting" @click="handleDailyExport">导出</el-button>
            </div>
          </el-form>
        </div>

        <div class="daily-kpi" v-if="dailySummary">
          <div class="kpi-card kpi-card--expected" :class="{ 'is-active': !daily.status }" @click="filterDailyStatus('')">
            <div class="kpi-label">应到</div>
            <div class="kpi-value">{{ dailySummary.ExpectedCount || 0 }}</div>
          </div>
          <div class="kpi-card kpi-card--done" :class="{ 'is-active': daily.status === 'Completed' }" @click="filterDailyStatus('Completed')">
            <div class="kpi-label">已打</div>
            <div class="kpi-value">{{ dailySummary.CompletedCount || 0 }}</div>
          </div>
          <div class="kpi-card kpi-card--missing" :class="{ 'is-active': daily.status === 'Missing' }" @click="filterDailyStatus('Missing')">
            <div class="kpi-label">未打</div>
            <div class="kpi-value">{{ dailySummary.MissingCount || 0 }}</div>
          </div>
          <div class="kpi-card kpi-card--exempt" :class="{ 'is-active': daily.status === 'Exempt' }" @click="filterDailyStatus('Exempt')">
            <div class="kpi-label">免签</div>
            <div class="kpi-value">{{ dailySummary.ExemptCount || 0 }}</div>
          </div>
          <div class="kpi-card kpi-card--rate">
            <div class="kpi-label">完成率</div>
            <div class="kpi-value">{{ dailyCompleteRate }}</div>
          </div>
          <div class="kpi-meta" v-if="dailySummary.ScheduleName">
            <span class="kpi-meta-schedule"><i class="el-icon-time"></i>{{ dailySummary.ScheduleName }}</span>
            <template v-if="isOccurrenceCancelled">
              <el-tooltip :content="`停办原因：${dailySummary.CancelReason || '—'}`" placement="top">
                <el-tag size="mini" type="info" effect="dark" class="cancelled-tag">
                  <i class="el-icon-remove-outline"></i> 已停办
                </el-tag>
              </el-tooltip>
              <el-button size="mini" type="primary" plain icon="el-icon-refresh-left" :loading="opRestoring" @click="handleRestoreOccurrence">恢复本场</el-button>
            </template>
            <el-button v-else size="mini" type="danger" plain icon="el-icon-remove-outline" @click="openCancelOccurrenceDialog">停办本场</el-button>
          </div>
        </div>

        <div v-if="isOccurrenceCancelled" class="cancelled-banner">
          <i class="el-icon-warning-outline"></i>
          本场次已停办（{{ dailySummary.CancelReason || "未填写原因" }}），教职工无法打卡，按周统计已扣除该场次。
        </div>

        <div class="tbl-scroll-host">
          <el-table key="daily-table" :data="dailyRows" v-loading="dailyLoading" stripe height="100%" :empty-text="dailyEmptyText">
            <el-table-column prop="TeacherName" label="教职工" min-width="110" align="center"></el-table-column>
            <el-table-column label="部门" min-width="140" align="center">
              <template slot-scope="{ row }">
                <div class="multi-cell">
                  <div class="multi-cell-main">
                    <span class="multi-cell-name" :title="row.DepartmentName || ''">{{ row.DepartmentName || "—" }}</span>
                  </div>
                  <div v-if="row.Departments && row.Departments.length > 1" class="multi-cell-btn-wrap">
                    <el-button type="text" size="small" icon="el-icon-office-building" class="multi-link-btn" @click.stop="openDeptListDialog(row)">
                      共 {{ row.Departments.length }} 个部门
                    </el-button>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="ClassName" label="班级/类型" min-width="110" align="center"></el-table-column>
            <el-table-column label="任教班级" min-width="150" align="center">
              <template slot-scope="{ row }">
                <div v-if="row.TeachingClasses && row.TeachingClasses.length" class="multi-cell">
                  <div class="multi-cell-main">
                    <span class="multi-cell-name" :title="row.TeachingClasses[0].ClassName">{{ row.TeachingClasses[0].ClassName }}</span>
                    <el-tag v-if="row.TeachingClasses[0].IsHeadTeacher" size="mini" effect="plain" class="head-teacher-tag">班主任</el-tag>
                  </div>
                  <div class="multi-cell-btn-wrap">
                    <el-button type="text" size="small" icon="el-icon-collection" class="multi-link-btn" @click.stop="openClassListDialog(row)">
                      共 {{ row.TeachingClasses.length }} 个班级
                    </el-button>
                  </div>
                </div>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" :type="statusTagType(scope.row.Status)">{{ statusText(scope.row.Status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="打卡时间" min-width="160" align="center">
              <template slot-scope="scope">
                <span>{{ formatDateTime(scope.row.PunchTime) || "—" }}</span>
                <el-tag v-if="scope.row.IsMakeup" size="mini" type="warning" effect="plain" class="makeup-tag">补卡</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="免签原因" min-width="140" align="center" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.ExemptReason || "—" }}</template>
            </el-table-column>
            <el-table-column label="操作" width="110" align="center" fixed="right">
              <template slot-scope="{ row }">
                <span v-if="isOccurrenceCancelled" class="op-muted">已停办</span>
                <el-button
                  v-else-if="row.Status === 'Exempt'"
                  type="text"
                  size="small"
                  icon="el-icon-refresh-left"
                  :loading="opRowLoadingId === row.ParticipantId"
                  @click="handleCancelExempt(row)">取消免签</el-button>
                <el-button
                  v-else-if="row.Status === 'Missing'"
                  type="text"
                  size="small"
                  icon="el-icon-document-checked"
                  class="op-exempt-btn"
                  @click="openExemptDialog(row)">设为免签</el-button>
                <span v-else class="op-muted">—</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination-container">
          <el-pagination
            layout="total, sizes, prev, pager, next"
            :current-page="daily.page"
            :page-size="daily.pageSize"
            :page-sizes="[20, 50, 100]"
            :total="dailyTotal"
            style="float: right"
            @size-change="handleDailySizeChange"
            @current-change="handleDailyPageChange">
          </el-pagination>
        </div>
      </template>
    </section>

    <!-- 按周汇总下钻：打卡记录弹窗（不跳转页面）。创建于 2026-10-04 -->
    <el-dialog
      :title="`打卡记录 - ${recordDialog.row ? recordDialog.row.TeacherName : ''}`"
      :visible.sync="recordDialog.visible"
      width="860px"
      top="8vh"
      append-to-body
      custom-class="record-view-dialog">
      <div v-if="recordDialog.row" class="rv-head">
        <div class="rv-meta">
          <span><i class="el-icon-date"></i>{{ formatDate(recordDialog.row.WeekStartDate) }} ~ {{ formatDate(recordDialog.row.WeekEndDate) }}</span>
          <span><i class="el-icon-s-flag"></i>{{ recordDialog.row.ItemName }}</span>
          <span><i class="el-icon-user"></i>{{ recordDialog.row.ClassName }}</span>
        </div>
        <div class="rv-stats">
          <span>参考应打 <b>{{ recordDialog.row.RequiredCount }}</b></span>
          <span>已打 <b class="is-done">{{ recordDialog.row.TotalCount }}</b></span>
          <span v-if="recordDialog.row.ExemptCount">免签 <b>{{ recordDialog.row.ExemptCount }}</b></span>
          <span>参考缺次 <b :class="{ 'is-miss': recordDialog.row.MissingCount > 0 }">{{ recordDialog.row.MissingCount }}</b></span>
        </div>
      </div>
      <div class="rv-tabs">
        <div
          v-for="tab in recordTabs"
          :key="tab.key"
          class="rv-tab"
          :class="{ 'is-active': recordDialog.filter === tab.key }"
          @click="switchRecordFilter(tab.key)">
          {{ tab.label }}<span class="rv-tab-count">{{ tab.count }}</span>
        </div>
      </div>
      <el-table
        ref="recordTable"
        :data="recordDialog.rows"
        v-loading="recordDialog.loading"
        size="small"
        max-height="420"
        row-key="Id"
        :expand-row-keys="recordDialog.expandedKeys"
        empty-text="暂无打卡记录"
        @expand-change="onRecordExpand">
        <el-table-column type="expand" width="1">
          <template slot-scope="{ row }">
            <div class="rv-photos" v-loading="recordDialog.photoLoadingId === row.Id">
              <template v-if="recordPhotos(row).length">
                <oss-el-image
                  v-for="(url, idx) in recordPhotos(row)"
                  :key="idx"
                  :src="url"
                  :preview-src-list="recordPhotos(row)"
                  class="rv-photo"
                  fit="cover">
                </oss-el-image>
              </template>
              <span v-else-if="recordDialog.photoLoadingId !== row.Id" class="rv-empty">暂无照片</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="考勤日期" width="120" align="center">
          <template slot-scope="{ row }">
            <div>{{ formatDate(row.AttendanceDate) }}</div>
            <div class="rv-sub">{{ weekDayText(row.AttendanceDate) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="时段" min-width="140" align="center">
          <template slot-scope="{ row }">
            <div>{{ row.ScheduleName || "—" }}</div>
            <div v-if="row.ScheduleStart" class="rv-sub">{{ shortTime(row.ScheduleStart) }} - {{ shortTime(row.ScheduleEnd) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="打卡时间" width="160" align="center">
          <template slot-scope="{ row }">{{ formatDateTime(row.PunchTime) }}</template>
        </el-table-column>
        <el-table-column label="类型" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.IsMakeup" size="mini" type="warning" effect="plain">补卡</el-tag>
            <el-tag v-else size="mini" type="success" effect="plain">正常</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="定位" min-width="160" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.Address || "—" }}</template>
        </el-table-column>
        <el-table-column label="照片" width="100" align="center">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.PhotoCount > 0"
              type="text"
              size="small"
              :icon="isRecordExpanded(row) ? 'el-icon-arrow-up' : 'el-icon-picture-outline'"
              @click="toggleRecordPhotos(row)">
              {{ isRecordExpanded(row) ? "收起" : `${row.PhotoCount} 张` }}
            </el-button>
            <span v-else class="op-muted">无</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button size="small" @click="recordDialog.visible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 停办场次 / 设置免签：填写原因。创建于 2026-10-04 -->
    <el-dialog
      :title="opDialog.mode === 'cancel' ? '停办本场' : '设为免签'"
      :visible.sync="opDialog.visible"
      width="460px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="op-reason-dialog">
      <div class="op-target">
        <template v-if="opDialog.mode === 'cancel'">
          <div class="op-target-row"><span>项目</span><b>{{ dailySummary && dailySummary.ItemName }}</b></div>
          <div class="op-target-row"><span>时段</span><b>{{ dailySummary && dailySummary.ScheduleName }}</b></div>
          <div class="op-target-row"><span>日期</span><b>{{ daily.date }}</b></div>
          <div class="op-tip">停办后全部人员无法打卡，按周统计会扣除该场次，可随时恢复。</div>
        </template>
        <template v-else>
          <div class="op-target-row"><span>教职工</span><b>{{ opDialog.row && opDialog.row.TeacherName }}</b></div>
          <div class="op-target-row"><span>项目</span><b>{{ dailySummary && dailySummary.ItemName }} · {{ dailySummary && dailySummary.ScheduleName }}</b></div>
          <div class="op-target-row"><span>日期</span><b>{{ daily.date }}</b></div>
          <div class="op-tip">免签后该人员本场无需打卡，按周统计会扣除 1 次应打，可随时取消。</div>
        </template>
      </div>
      <div class="op-reason-label"><span class="op-required">*</span>{{ opDialog.mode === "cancel" ? "停办原因" : "免签原因" }}</div>
      <div class="op-quick">
        <el-tag
          v-for="r in opQuickReasons"
          :key="r"
          size="small"
          :effect="opDialog.reason === r ? 'dark' : 'plain'"
          class="op-quick-tag"
          @click="opDialog.reason = r">{{ r }}</el-tag>
      </div>
      <el-input
        v-model="opDialog.reason"
        type="textarea"
        :rows="3"
        maxlength="200"
        show-word-limit
        placeholder="请输入原因"></el-input>
      <span slot="footer">
        <el-button size="small" @click="opDialog.visible = false">取 消</el-button>
        <el-button
          size="small"
          :type="opDialog.mode === 'cancel' ? 'danger' : 'primary'"
          :loading="opDialog.saving"
          @click="submitOpDialog">{{ opDialog.mode === "cancel" ? "确认停办" : "确认免签" }}</el-button>
      </span>
    </el-dialog>

    <!-- 多部门 / 多任教班级查看弹窗，样式参考教师信息管理。创建于 2026-10-04 -->
    <el-dialog
      :title="listDialog.title"
      :visible.sync="listDialog.visible"
      :width="listDialog.showSubject ? '760px' : '520px'"
      append-to-body
      custom-class="multi-view-dialog">
      <div class="multi-view-summary">
        <span>教职工：<b>{{ listDialog.teacherName }}</b></span>
        <span>{{ listDialog.mainLabel }}：<b class="is-primary">{{ listDialog.mainName || "—" }}</b></span>
        <span>总数：<b class="is-primary">{{ listDialog.rows.length }} 个</b></span>
      </div>
      <el-table :data="listDialog.rows" size="small" max-height="340" border stripe>
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        <el-table-column prop="name" :label="listDialog.nameLabel" min-width="160" align="center"></el-table-column>
        <el-table-column v-if="listDialog.showSubject" label="任教科目" min-width="260">
          <template slot-scope="{ row }">
            <div v-if="row.subjects && row.subjects.length" class="subject-chip-wrap">
              <el-tag
                v-for="(name, index) in row.subjects"
                :key="index"
                size="mini"
                effect="plain"
                class="subject-chip">{{ name }}</el-tag>
            </div>
            <span v-else class="subject-empty">—</span>
          </template>
        </el-table-column>
        <el-table-column label="性质" width="120" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.isMain" size="small" type="primary" effect="dark">
              <i class="el-icon-star-on"></i> {{ listDialog.mainTagText }}
            </el-tag>
            <el-tag v-else size="small" type="info" effect="plain">{{ listDialog.otherTagText }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button @click="listDialog.visible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from "../../components/DepartmentPane";
import {
  getHeadTeacherWorkAttendanceItemOptions,
  getHeadTeacherWorkAttendanceSummaryPage,
  exportHeadTeacherWorkAttendanceSummary,
  getHeadTeacherWorkAttendanceDailySummary,
  getHeadTeacherWorkAttendanceDailyDetails,
  exportHeadTeacherWorkAttendanceDaily,
  getHeadTeacherWorkAttendanceRecordPage,
  getHeadTeacherWorkAttendanceRecordDetail,
  setHeadTeacherWorkAttendanceOccurrenceStatus,
  setHeadTeacherWorkAttendanceParticipantExempt,
  downloadHeadTeacherWorkAttendanceBlob,
  resolveHeadTeacherWorkAttendanceSchoolIdFromTreeNode,
  onHeadTeacherWorkAttendanceSchoolChange
} from "@/api/headTeacherWorkAttendance";
import { treeNodeDeptId } from '../../utils/deptCascaderProps';

/**
 * 按输入框视口坐标摆正日期弹层。
 * 汇总页在分栏和滚动容器里时，Element 会把弹层算到页面左上角。最后修改：2026-10-13
 * @param {HTMLElement} input
 * @param {HTMLElement} panel
 * @returns {Boolean}
 */
function placeDatePanelNearInput(input, panel) {
  if (!input || !panel || !input.getBoundingClientRect) return false;
  const style = window.getComputedStyle(panel);
  if (style.display === "none" || style.visibility === "hidden") return false;
  const rect = input.getBoundingClientRect();
  if (!rect.width && !rect.height) return false;
  const width = panel.offsetWidth || 0;
  const height = panel.offsetHeight || 0;
  const gap = 6;
  let left = rect.left;
  let top = rect.bottom + gap;
  const maxLeft = Math.max(8, window.innerWidth - 8);
  const maxTop = Math.max(8, window.innerHeight - 8);
  if (width && left + width > maxLeft) left = Math.max(8, maxLeft - width);
  if (left < 8) left = 8;
  if (height && top + height > maxTop && rect.top - height - gap >= 8) {
    top = rect.top - height - gap;
  }
  const leftPx = Math.round(left) + "px";
  const topPx = Math.round(top) + "px";
  if (panel.style.position === "fixed" && panel.style.left === leftPx && panel.style.top === topPx) {
    return true;
  }
  panel.style.position = "fixed";
  panel.style.left = leftPx;
  panel.style.top = topPx;
  panel.style.margin = "0";
  panel.style.transform = "none";
  return true;
}

function todayStr() {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

function currentWeekRange() {
  const today = new Date();
  const day = today.getDay() || 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - day + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const format = date => date.getFullYear()
    + "-" + String(date.getMonth() + 1).padStart(2, "0")
    + "-" + String(date.getDate()).padStart(2, "0");
  return [format(monday), format(sunday)];
}

const statusMap = {
  Completed: { text: "已打", type: "success" },
  Missing: { text: "未打", type: "danger" },
  Exempt: { text: "免签", type: "info" }
};

export default {
  components: { DepartmentPane },
  data() {
    const today = todayStr();
    return {
      schoolId: 0,
      DepartmentId: 0,
      scopeDepartmentId: 0,
      viewMode: "weekly",
      itemOptions: [],
      filters: {
        dateRange: currentWeekRange(),
        teacherName: "",
        itemId: null,
        isMakeup: null
      },
      rows: [],
      page: 1,
      pageSize: 10,
      total: 0,
      listLoading: false,
      exporting: false,
      daily: {
        date: today,
        itemId: null,
        status: "",
        teacherName: "",
        page: 1,
        pageSize: 20
      },
      dailyStatusOptions: [
        { value: "Completed", label: "已打" },
        { value: "Missing", label: "未打" },
        { value: "Exempt", label: "免签" }
      ],
      dailySummary: null,
      listDialog: {
        visible: false,
        title: "",
        teacherName: "",
        mainLabel: "",
        mainName: "",
        nameLabel: "",
        mainTagText: "",
        otherTagText: "",
        showSubject: false,
        rows: []
      },
      opDialog: {
        visible: false,
        mode: "exempt",
        row: null,
        reason: "",
        saving: false
      },
      opRestoring: false,
      opRowLoadingId: null,
      recordDialog: {
        visible: false,
        loading: false,
        row: null,
        filter: "all",
        allRows: [],
        rows: [],
        expandedKeys: [],
        photoMap: {},
        photoLoadingId: null
      },
      dailyRows: [],
      dailyTotal: 0,
      dailyLoading: false,
      dailyExporting: false
    };
  },
  beforeDestroy() {
    this.stopDatePanelAlign();
  },
  computed: {
    recordTabs() {
      const all = this.recordDialog.allRows;
      return [
        { key: "all", label: "全部", count: all.length },
        { key: "normal", label: "正常", count: all.filter(x => !x.IsMakeup).length },
        { key: "makeup", label: "补卡", count: all.filter(x => x.IsMakeup).length }
      ];
    },
    isOccurrenceCancelled() {
      return !!(this.dailySummary && this.dailySummary.OccurrenceStatus === 2);
    },
    opQuickReasons() {
      return this.opDialog.mode === "cancel"
        ? ["天气原因", "活动调整", "节假日", "临时取消"]
        : ["病假", "事假", "外出培训", "公务外出"];
    },
    dailyCompleteRate() {
      const s = this.dailySummary;
      if (!s) return "—";
      const base = (s.ExpectedCount || 0) - (s.ExemptCount || 0);
      if (base <= 0) return "—";
      return Math.round(((s.CompletedCount || 0) / base) * 1000) / 10 + "%";
    },
    dailyEmptyText() {
      if (!this.schoolId) return "请先在左侧选择学校";
      if (!this.itemOptions.length) return "暂无考勤项目，请先到考勤配置中新增";
      if (!this.daily.itemId) return "请选择项目";
      if (!this.dailySummary) return "该日没有此项目的考勤时段";
      return "暂无数据";
    }
  },
  methods: {
    /**
     * 日期弹层打开后，持续按输入框位置校正，避免被 Popper 写回左上角。最后修改：2026-10-13
     * @param {String} refName
     */
    alignDatePanel(refName) {
      const run = () => {
        const picker = this.$refs[refName];
        const input = picker && picker.$el;
        const panel = document.querySelector(".htwa-date-popper");
        if (!input || !panel) return;
        placeDatePanelNearInput(input, panel);
        this.stopDatePanelAlign();
        this.datePanelObserver = new MutationObserver(() => {
          placeDatePanelNearInput(input, panel);
        });
        this.datePanelObserver.observe(panel, { attributes: true, attributeFilter: ["style"] });
      };
      this.$nextTick(() => {
        this.$nextTick(() => {
          requestAnimationFrame(run);
        });
      });
    },
    stopDatePanelAlign() {
      if (this.datePanelObserver) {
        this.datePanelObserver.disconnect();
        this.datePanelObserver = null;
      }
    },
    formatDate(val) {
      return val ? String(val).substring(0, 10) : "";
    },
    formatDateTime(val) {
      return val ? String(val).replace("T", " ").substring(0, 19) : "";
    },
    /** 查看教职工全部内部部门（主属排前）。创建于 2026-10-04 */
    openDeptListDialog(row) {
      const rows = (row.Departments || []).map(d => ({ name: d.DepartmentName, isMain: !!d.IsDefault }));
      rows.sort((a, b) => Number(b.isMain) - Number(a.isMain));
      this.listDialog = {
        visible: true,
        title: `查看内部部门 - ${row.TeacherName || "教职工"}`,
        teacherName: row.TeacherName || "—",
        mainLabel: "主属部门",
        mainName: row.DepartmentName,
        nameLabel: "内部部门名称",
        mainTagText: "主属部门",
        otherTagText: "兼任部门",
        showSubject: false,
        rows
      };
    },
    /** 将「美术、语文、体育与健康2」拆成标签，避免长串把表格撑乱。创建于 2026-10-04 */
    splitSubjectNames(text) {
      return String(text || "")
        .split(/[、,，;；]+/)
        .map(x => x.trim())
        .filter(Boolean);
    },
    /** 查看教职工全部任教班级（当前行班级排前）。创建于 2026-10-04 */
    openClassListDialog(row) {
      const rows = (row.TeachingClasses || []).map(c => ({
        name: c.ClassName,
        subjects: this.splitSubjectNames(c.SubjectNames),
        isMain: !!c.IsHeadTeacher
      }));
      const head = rows.filter(x => x.isMain).map(x => x.name);
      this.listDialog = {
        visible: true,
        title: `查看任教班级 - ${row.TeacherName || "教职工"}`,
        teacherName: row.TeacherName || "—",
        mainLabel: "班主任班级",
        mainName: head.join("、"),
        nameLabel: "班级名称",
        mainTagText: "班主任",
        otherTagText: "任课",
        showSubject: true,
        rows
      };
    },
    statusText(status) {
      return (statusMap[status] && statusMap[status].text) || status || "—";
    },
    statusTagType(status) {
      return (statusMap[status] && statusMap[status].type) || "";
    },
    async treeNodeClick(data, node) {
      const schId = resolveHeadTeacherWorkAttendanceSchoolIdFromTreeNode(node, data && data.value);
      const scopeId = treeNodeDeptId(data) || schId;
      onHeadTeacherWorkAttendanceSchoolChange(this, schId, scopeId);
      this.dailyRows = [];
      this.dailySummary = null;
      this.dailyTotal = 0;
      if (!schId) return this.$message.warning("请选择学校或其下级节点");
      await this.loadItems();
      this.reloadCurrentMode();
    },
    async loadItems() {
      this.itemOptions = await getHeadTeacherWorkAttendanceItemOptions(this.schoolId);
      const first = this.itemOptions.find(x => x.IsEnabled !== false) || this.itemOptions[0];
      const defaultId = first ? first.Id : null;
      if (!this.itemOptions.some(x => x.Id === this.daily.itemId)) this.daily.itemId = defaultId;
      if (!this.itemOptions.some(x => x.Id === this.filters.itemId)) this.filters.itemId = defaultId;
    },
    switchMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.reloadCurrentMode();
    },
    reloadCurrentMode() {
      if (!this.schoolId) return;
      if (this.viewMode === "daily") this.handleDailyQuery();
      else this.handleQuery();
    },

    /* ---------- 按周汇总 ---------- */
    buildParams() {
      const range = this.filters.dateRange || [];
      return {
        SchId: this.schoolId,
        ScopeDepartmentId: this.scopeDepartmentId || this.DepartmentId || null,
        StartDate: range[0] || null,
        EndDate: range[1] || null,
        TeacherName: (this.filters.teacherName || "").trim() || null,
        ItemId: this.filters.itemId || null,
        IsMakeup: this.filters.isMakeup,
        Page: this.page,
        PageSize: this.pageSize
      };
    },
    handleQuery() {
      this.page = 1;
      this.loadData();
    },
    handleCurrentChange(page) {
      this.page = page;
      this.loadData();
    },
    async loadData() {
      if (!this.schoolId) return this.$message.warning("请先选择学校");
      if (!this.filters.itemId) {
        this.rows = [];
        this.total = 0;
        return this.$message.warning(this.itemOptions.length ? "请选择项目" : "暂无考勤项目，请先到考勤配置中新增");
      }
      this.listLoading = true;
      try {
        const res = await getHeadTeacherWorkAttendanceSummaryPage(this.buildParams());
        if (res.data && res.data.success) {
          const pageData = res.data.response || {};
          this.rows = pageData.data || [];
          this.total = pageData.dataCount || 0;
        } else {
          this.rows = [];
          this.total = 0;
          this.$message.error((res.data && res.data.msg) || "查询失败");
        }
      } finally {
        this.listLoading = false;
      }
    },
    /**
     * 按周汇总下钻：弹窗展示该人该项目本周的打卡记录（一次拉全，弹窗内切换全部/正常/补卡）。创建于 2026-10-04
     * @param {Object} row - 周汇总行
     * @param {Boolean|null} isMakeup - 初始筛选：null 全部、false 正常、true 补卡
     */
    async openRecordDialog(row, isMakeup) {
      this.recordDialog = {
        visible: true,
        loading: true,
        row,
        filter: isMakeup === true ? "makeup" : isMakeup === false ? "normal" : "all",
        allRows: [],
        rows: [],
        expandedKeys: [],
        photoMap: {},
        photoLoadingId: null
      };
      try {
        const res = await getHeadTeacherWorkAttendanceRecordPage({
          SchId: this.schoolId,
          StartDate: this.formatDate(row.WeekStartDate),
          EndDate: this.formatDate(row.WeekEndDate),
          TeacherMasterId: row.TeacherMasterId,
          ClassId: row.ClassId,
          ItemId: row.ItemId,
          Page: 1,
          PageSize: 200
        });
        if (res.data && res.data.success) {
          const list = (res.data.response && res.data.response.data) || [];
          this.recordDialog.allRows = list
            .slice()
            .sort((a, b) => String(a.PunchTime).localeCompare(String(b.PunchTime)));
          this.applyRecordFilter();
        } else {
          this.$message.error((res.data && res.data.msg) || "加载打卡记录失败");
        }
      } finally {
        this.recordDialog.loading = false;
      }
    },
    switchRecordFilter(key) {
      this.recordDialog.filter = key;
      this.applyRecordFilter();
    },
    applyRecordFilter() {
      const { filter, allRows } = this.recordDialog;
      this.recordDialog.rows = filter === "normal"
        ? allRows.filter(x => !x.IsMakeup)
        : filter === "makeup" ? allRows.filter(x => x.IsMakeup) : allRows;
    },
    isRecordExpanded(row) {
      return this.recordDialog.expandedKeys.includes(row.Id);
    },
    recordPhotos(row) {
      return this.recordDialog.photoMap[row.Id] || [];
    },
    toggleRecordPhotos(row) {
      this.$refs.recordTable && this.$refs.recordTable.toggleRowExpansion(row);
    },
    /** 展开行时按需加载该记录全部照片 */
    async onRecordExpand(row, expandedRows) {
      this.recordDialog.expandedKeys = expandedRows.map(x => x.Id);
      if (!this.isRecordExpanded(row) || this.recordDialog.photoMap[row.Id]) return;
      this.recordDialog.photoLoadingId = row.Id;
      try {
        const res = await getHeadTeacherWorkAttendanceRecordDetail(this.schoolId, row.Id);
        const photos = (res.data && res.data.success && res.data.response && res.data.response.Photos) || [];
        this.$set(this.recordDialog.photoMap, row.Id, photos.map(p => p.FileUrl).filter(Boolean));
      } finally {
        this.recordDialog.photoLoadingId = null;
      }
    },
    weekDayText(val) {
      if (!val) return "";
      const d = new Date(String(val).substring(0, 10).replace(/-/g, "/"));
      return ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][d.getDay()];
    },
    shortTime(val) {
      return val ? String(val).substring(0, 5) : "";
    },
    async handleExport() {
      if (!this.schoolId) return this.$message.warning("请先选择学校");
      if (!this.filters.itemId) return this.$message.warning("请选择项目");
      this.exporting = true;
      try {
        const params = this.buildParams();
        params.Page = 1;
        params.PageSize = 9999;
        const res = await exportHeadTeacherWorkAttendanceSummary(params);
        downloadHeadTeacherWorkAttendanceBlob(res, "教职工工作考勤周汇总.xlsx");
      } catch (e) {
        this.$message.error("导出失败");
      } finally {
        this.exporting = false;
      }
    },

    /* ---------- 按日统计 ---------- */
    buildDailyParams() {
      return {
        SchId: this.schoolId,
        ScopeDepartmentId: this.scopeDepartmentId || this.DepartmentId || null,
        Date: this.daily.date,
        ItemId: this.daily.itemId,
        TeacherName: (this.daily.teacherName || "").trim() || null,
        DetailStatus: this.daily.status || null,
        Page: this.daily.page,
        PageSize: this.daily.pageSize
      };
    },
    handleDailyQuery() {
      this.daily.page = 1;
      this.loadDaily(true);
    },
    handleDailyPageChange(page) {
      this.daily.page = page;
      this.loadDaily(false);
    },
    handleDailySizeChange(size) {
      this.daily.pageSize = size;
      this.daily.page = 1;
      this.loadDaily(false);
    },
    filterDailyStatus(status) {
      this.daily.status = status;
      this.daily.page = 1;
      this.loadDaily(false);
    },
    /** 按日查询：refreshSummary 为 true 时同时刷新顶部统计卡片 */
    async loadDaily(refreshSummary) {
      if (!this.schoolId) return this.$message.warning("请先选择学校");
      if (!this.daily.itemId) {
        this.dailyRows = [];
        this.dailySummary = null;
        this.dailyTotal = 0;
        return;
      }
      this.dailyLoading = true;
      try {
        const params = this.buildDailyParams();
        const tasks = [getHeadTeacherWorkAttendanceDailyDetails(params)];
        if (refreshSummary || !this.dailySummary) {
          tasks.push(getHeadTeacherWorkAttendanceDailySummary({
            SchId: params.SchId,
            ScopeDepartmentId: params.ScopeDepartmentId,
            Date: params.Date,
            ItemId: params.ItemId
          }));
        }
        const [detailRes, summaryRes] = await Promise.all(tasks);
        if (summaryRes) {
          this.dailySummary = (summaryRes.data && summaryRes.data.success && summaryRes.data.response) || null;
        }
        if (detailRes.data && detailRes.data.success) {
          const pageData = detailRes.data.response || {};
          this.dailyRows = pageData.data || [];
          this.dailyTotal = pageData.dataCount || 0;
        } else {
          this.dailyRows = [];
          this.dailyTotal = 0;
          this.$message.error((detailRes.data && detailRes.data.msg) || "查询失败");
        }
      } finally {
        this.dailyLoading = false;
      }
    },
    /** 打开停办本场弹窗。创建于 2026-10-04 */
    openCancelOccurrenceDialog() {
      if (!this.dailySummary || !this.dailySummary.OccurrenceId) {
        return this.$message.warning("当前没有可停办的场次");
      }
      this.opDialog = { visible: true, mode: "cancel", row: null, reason: "", saving: false };
    },
    /** 打开设为免签弹窗。创建于 2026-10-04 */
    openExemptDialog(row) {
      this.opDialog = { visible: true, mode: "exempt", row, reason: "", saving: false };
    },
    /** 提交停办 / 免签。创建于 2026-10-04 */
    async submitOpDialog() {
      const reason = (this.opDialog.reason || "").trim();
      if (!reason) return this.$message.warning(this.opDialog.mode === "cancel" ? "请填写停办原因" : "请填写免签原因");
      this.opDialog.saving = true;
      try {
        const res = this.opDialog.mode === "cancel"
          ? await setHeadTeacherWorkAttendanceOccurrenceStatus({
            SchId: this.schoolId,
            OccurrenceId: this.dailySummary.OccurrenceId,
            Enabled: false,
            Reason: reason
          })
          : await setHeadTeacherWorkAttendanceParticipantExempt({
            SchId: this.schoolId,
            ParticipantId: this.opDialog.row.ParticipantId,
            Enabled: false,
            Reason: reason
          });
        if (res.data && res.data.success) {
          this.$message.success(this.opDialog.mode === "cancel" ? "已停办本场" : "已设为免签");
          this.opDialog.visible = false;
          await this.loadDaily(true);
        } else {
          this.$message.error((res.data && res.data.msg) || "操作失败");
        }
      } finally {
        this.opDialog.saving = false;
      }
    },
    /** 恢复已停办场次。创建于 2026-10-04 */
    async handleRestoreOccurrence() {
      try {
        await this.$confirm(`确认恢复「${this.dailySummary.ItemName} · ${this.dailySummary.ScheduleName}」${this.daily.date} 的场次？恢复后教职工可正常打卡。`, "恢复本场", {
          type: "warning",
          confirmButtonText: "确认恢复",
          cancelButtonText: "取消"
        });
      } catch (e) {
        return;
      }
      this.opRestoring = true;
      try {
        const res = await setHeadTeacherWorkAttendanceOccurrenceStatus({
          SchId: this.schoolId,
          OccurrenceId: this.dailySummary.OccurrenceId,
          Enabled: true
        });
        if (res.data && res.data.success) {
          this.$message.success("已恢复本场");
          await this.loadDaily(true);
        } else {
          this.$message.error((res.data && res.data.msg) || "操作失败");
        }
      } finally {
        this.opRestoring = false;
      }
    },
    /** 取消人员免签。创建于 2026-10-04 */
    async handleCancelExempt(row) {
      try {
        await this.$confirm(`确认取消「${row.TeacherName}」的免签？取消后需正常打卡。`, "取消免签", {
          type: "warning",
          confirmButtonText: "确认取消",
          cancelButtonText: "返回"
        });
      } catch (e) {
        return;
      }
      this.opRowLoadingId = row.ParticipantId;
      try {
        const res = await setHeadTeacherWorkAttendanceParticipantExempt({
          SchId: this.schoolId,
          ParticipantId: row.ParticipantId,
          Enabled: true
        });
        if (res.data && res.data.success) {
          this.$message.success("已取消免签");
          await this.loadDaily(true);
        } else {
          this.$message.error((res.data && res.data.msg) || "操作失败");
        }
      } finally {
        this.opRowLoadingId = null;
      }
    },
    async handleDailyExport() {
      if (!this.schoolId) return this.$message.warning("请先选择学校");
      if (!this.daily.itemId) return this.$message.warning("请选择项目");
      this.dailyExporting = true;
      try {
        const params = this.buildDailyParams();
        params.Page = 1;
        params.PageSize = 9999;
        const res = await exportHeadTeacherWorkAttendanceDaily(params);
        downloadHeadTeacherWorkAttendanceBlob(res, `教职工工作考勤日统计-${this.daily.date}.xlsx`);
      } catch (e) {
        this.$message.error("导出失败");
      } finally {
        this.dailyExporting = false;
      }
    }
  }
};
</script>

<style scoped>
.toolbar-field-w-md.htwa-item-select {
  width: 200px !important;
}
.summary-right {
  --htwa-accent: #0891b2;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.summary-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.mode-seg {
  display: inline-flex;
  padding: 3px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.mode-seg-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mode-seg-item:hover {
  color: #0e7490;
}

.mode-seg-item.is-active {
  background: #fff;
  color: var(--htwa-accent);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.mode-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 240px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.mode-hint i {
  color: var(--htwa-accent);
}

.daily-kpi {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.kpi-card {
  min-width: 110px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
}

.kpi-card.is-active {
  box-shadow: 0 0 0 2px currentColor inset;
}

.kpi-card--rate {
  cursor: default;
}

.kpi-label {
  font-size: 12px;
  color: #64748b;
}

.kpi-value {
  margin-top: 2px;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
}

.kpi-card--expected { color: #334155; }
.kpi-card--done { color: #16a34a; }
.kpi-card--missing { color: #dc2626; }
.kpi-card--exempt { color: #64748b; }
.kpi-card--rate { color: var(--htwa-accent); }

.kpi-card .kpi-value { color: inherit; }

.kpi-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  font-size: 12px;
  color: #64748b;
}

.makeup-tag {
  margin-left: 6px;
}

/* 停办 / 免签操作。创建于 2026-10-04 */
.kpi-meta-schedule {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.cancelled-tag {
  border-radius: 10px;
}

.cancelled-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #b45309;
  font-size: 13px;
}

.op-muted {
  color: #c0c4cc;
  font-size: 12px;
}

.op-exempt-btn {
  color: #64748b;
}

.op-exempt-btn:hover,
.op-exempt-btn:focus {
  color: #2563eb;
}

/* 多部门 / 多班级单元格：列宽不够时省略，不拆字、不溢到相邻列。最后修改：2026-10-04 */
.tbl-scroll-host >>> .el-table .cell {
  overflow: hidden;
}

.multi-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.multi-cell-main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #303133;
}

.multi-cell-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multi-cell-btn-wrap {
  display: flex;
  justify-content: center;
  max-width: 100%;
}

.multi-link-btn {
  padding: 3px 8px;
  font-size: 12px;
  line-height: 16px;
  color: #2563eb;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  white-space: nowrap;
  transition: all 0.2s;
}

.multi-link-btn:hover,
.multi-link-btn:focus {
  color: #1d4ed8;
  background-color: #dbeafe;
  border-color: #93c5fd;
}

.cancelled-hint {
  margin-left: 4px;
  font-size: 12px;
  color: #e6a23c;
}

.head-teacher-tag {
  flex-shrink: 0;
  margin-left: 0;
  color: #2563eb;
  border-color: #bfdbfe;
  background-color: #eff6ff;
}
</style>

<style>
.multi-view-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.multi-view-dialog .el-dialog__body {
  padding: 12px 20px 8px;
}

.multi-view-dialog .multi-view-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  font-size: 13px;
  color: #64748b;
}

.multi-view-dialog .multi-view-summary b {
  color: #303133;
  font-weight: 600;
}

.multi-view-dialog .multi-view-summary b.is-primary {
  color: #2563eb;
}

.multi-view-dialog .subject-chip-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
  padding: 4px 0;
}

.multi-view-dialog .subject-chip {
  max-width: 100%;
  height: auto;
  padding: 0 8px;
  line-height: 22px;
  white-space: normal;
  color: #334155;
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.multi-view-dialog .subject-empty {
  color: #c0c4cc;
}

.htwa-date-popper .popper__arrow {
  display: none;
}

.record-view-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.record-view-dialog .el-dialog__body {
  padding: 12px 20px 6px;
}

.record-view-dialog .rv-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  font-size: 13px;
}

.record-view-dialog .rv-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #303133;
}

.record-view-dialog .rv-meta i {
  margin-right: 4px;
  color: #94a3b8;
}

.record-view-dialog .rv-stats {
  display: flex;
  gap: 14px;
  color: #64748b;
}

.record-view-dialog .rv-stats b {
  margin-left: 2px;
  color: #303133;
  font-weight: 600;
}

.record-view-dialog .rv-stats b.is-done {
  color: #16a34a;
}

.record-view-dialog .rv-stats b.is-miss {
  color: #dc2626;
}

.record-view-dialog .rv-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: #f1f5f9;
}

.record-view-dialog .rv-tab {
  padding: 4px 14px;
  border-radius: 6px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.record-view-dialog .rv-tab.is-active {
  background: #fff;
  color: #2563eb;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.record-view-dialog .rv-tab-count {
  margin-left: 4px;
  font-size: 12px;
  opacity: 0.75;
}

.record-view-dialog .rv-sub {
  font-size: 12px;
  color: #94a3b8;
}

.record-view-dialog .el-table__expand-column .cell {
  display: none;
}

.record-view-dialog .rv-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 40px;
  padding: 4px 12px;
}

.record-view-dialog .rv-photo {
  width: 88px;
  height: 88px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.record-view-dialog .rv-empty {
  font-size: 12px;
  color: #94a3b8;
}

.record-view-dialog .op-muted {
  color: #c0c4cc;
  font-size: 12px;
}

.op-reason-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.op-reason-dialog .el-dialog__body {
  padding: 12px 20px 4px;
}

.op-reason-dialog .op-target {
  padding: 10px 12px;
  margin-bottom: 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}

.op-reason-dialog .op-target-row {
  display: flex;
  gap: 12px;
  line-height: 24px;
  font-size: 13px;
}

.op-reason-dialog .op-target-row span {
  width: 48px;
  flex-shrink: 0;
  color: #94a3b8;
}

.op-reason-dialog .op-target-row b {
  color: #303133;
  font-weight: 500;
}

.op-reason-dialog .op-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #b45309;
}

.op-reason-dialog .op-reason-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: #303133;
}

.op-reason-dialog .op-required {
  margin-right: 4px;
  color: #f56c6c;
}

.op-reason-dialog .op-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.op-reason-dialog .op-quick-tag {
  cursor: pointer;
}
</style>
